import { useState, useEffect, useCallback } from 'react';
import { getI18nLanguageFromLocalStorage, removeShoppingCartPaymentSuccessLocalStorage, removeShoppingCartStateFromLocalStorage } from '../../helpers/localStorageHelper';
import {
  getPaymentReturnHubConnection,
  initializePaymentReturnConnection,
  stopPaymentReturnConnection
} from '../../services/signalRService';
import { hasPaymentTransactionStatusChanged, reverifyPaymentTransaction } from '../../services/paymentTransactionService';
import { PAYMENT_METHODS } from '../../constants/payment-methods';
import LoadingButton from '../common/LoadingButton';
import { parse, stringify } from 'uuid';

const PaymentReturn = ({ props }) => {
  const currentLanguage = getI18nLanguageFromLocalStorage();
  const [loadingDots, setLoadingDots] = useState('');
  const [loading, setLoading] = useState(false);

  const normalizeGuid = (rawId) => {
    if (!rawId) return null;

    let candidate = rawId;

    // If it's 32 characters without dashes, format it first
    if (/^[0-9a-fA-F]{32}$/.test(rawId)) {
      candidate = [
        rawId.slice(0, 8),
        rawId.slice(8, 12),
        rawId.slice(12, 16),
        rawId.slice(16, 20),
        rawId.slice(20)
      ].join("-");
    }

    try {
      return stringify(parse(candidate));
    } catch {
      return rawId;
    }
  };

  const parsePaymentQuery = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Transaction ID (guid)
    let transactionId = null;
    const txId = urlParams.get("transactionId");

    if (txId) {
      transactionId = normalizeGuid(txId);
    } else {
      const vnpOrderInfo = urlParams.get("vnp_OrderInfo");
      if (vnpOrderInfo) {
        const parts = vnpOrderInfo.split("~");
        if (parts.length === 2) {
          transactionId = normalizeGuid(parts[0]);
        }
      }
    }

    // Order number
    let orderNumber = urlParams.get("vnp_TxnRef") || urlParams.get("merchant_uid") || urlParams.get("link_order_ref") || urlParams.get("paymentId");

    return { transactionId, orderNumber };
  }, []);

  useEffect(() => {
    let isMounted = true; // Cleanup flag
    const { transactionId, orderNumber } = parsePaymentQuery();

    const checkPaymentStatus = async () => {
      if (transactionId && orderNumber) {
        try {
          const result = await hasPaymentTransactionStatusChanged(transactionId);
          if (isMounted && result.changed) {
            if (result.isCompleted) {
              window.location.href = `/${currentLanguage}/receipt/${orderNumber}`; 
            } else {
              window.location.href = `/${currentLanguage}/shopping-cart`; 
            }
          }
        } catch (err) {
          console.error("Error checking payment status:", err);
        }
      }
    };

    checkPaymentStatus();

    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [currentLanguage, parsePaymentQuery]);

  useEffect(() => {
    const { transactionId, orderNumber } = parsePaymentQuery();
    if (!orderNumber || !transactionId) return;
    const connectAndListen = async () => {
      try {
        await initializePaymentReturnConnection(process.env.API_ENDPOINT);

        getPaymentReturnHubConnection().on('PaymentStatusUpdated', async (data) => {
          const result = await hasPaymentTransactionStatusChanged(transactionId);
          if (result.changed) {
            if (result.isCompleted) {
              window.location.href = `/${currentLanguage}/receipt/${orderNumber}`;
            } else {
              window.location.href = `/${currentLanguage}/shopping-cart`;
            }
          }
        });
      } catch (error) {

      }
    };

    connectAndListen();

    return () => {
      stopPaymentReturnConnection();
    };
  }, [currentLanguage, parsePaymentQuery]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingDots(prev => {
        if (prev === '..') return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    removeShoppingCartStateFromLocalStorage();
    removeShoppingCartPaymentSuccessLocalStorage();
  }, []);

  const handleRecheck = async () => {
    setLoading(true);
    try {
      const { orderNumber } = parsePaymentQuery();
      const urlParams = new URLSearchParams(window.location.search); 
      if (!urlParams.get('code')) {
        urlParams.set('code', PAYMENT_METHODS.VNPAY);
      }
      const queryString = '?' + urlParams.toString();
      const result = await reverifyPaymentTransaction(queryString);
      if (result.changed) {
        if (result.isCompleted) {
          window.location.href = `/${currentLanguage}/receipt/${orderNumber}`;
        } else {
          window.location.href = `/${currentLanguage}/shopping-cart`;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-4 text-center">
      <div className="register-success w-full flex flex-column align-items-center p-5 pb-8 text-center">
        <h3 className="keduall-typography heading-h3-semibold-600 mt-3 mb-5 text-center">{props?.pageTitle}</h3>
        <div>
          <img style={{maxHeight : '200px'}} src={props?.image} alt="" />
        </div>

        <div className="keduall-typography text-16-normal-400 mt-5 mb-3 flex items-center justify-center text-center">
          {props?.pageDescription}
          {(
            <span className="loading-dots w-1rem text-left">
              {loadingDots}
            </span>
          )}
        </div>
        <LoadingButton 
          loading={loading} 
          onClick={handleRecheck}
          label={props?.reVerifyBtn}
          className="keduall-buttons button-filled-default-small-primary keduall-typography text-14-semibold-600"
        />
      </div>
    </div>
  );
};

export default PaymentReturn;
